import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { dateToMidnightUTCObject } from '~/modules/common/dates/convert';
import { useMeContext } from '~/modules/me/useMeContext';
import {
  GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES,
  tryResourcePlanAllocatedUsersAndRolesCachedQuery
} from '~/modules/projects/resourcing-plan/ResourceRequestChart/hooks/useFetchAllocatedUsersAndRoles';
import { getTaskResourceUserAllocationsSummaryForUserQuery } from '~/modules/tasks/hooks/useGetTaskResourceUserAllocationsSummaryForUser';
import {
  ReleaseResourceAllocationType,
  ResourceRequestStatus,
  SpecificResourceRequestFragmentDoc
} from '~/types';
import RESOURCE_REQUESTS_ALLOCATIONS_QUERY from '~/modules/resourcing/common/fragments/resourceRequestsQuery';
import {
  resourceMatchesFragment,
  resourceRequestTotalsFragment,
  specificResourceAllocationFragment
} from '../../../resourcing/common/fragments';
import useResourcingChartToolbarContext from './useResourcingChartToolbarContext';

export const RELEASE_RESOURCE_ALLOCATION = gql`
  mutation releaseResourceAllocations(
    $input: BulkReleaseResourceAllocationsInput!
    $resourceMatchesPage: Int = 1
    $resourceMatchesPageSize: Int = 1
    $isPsaPraaResourceMatchingEnabled: Boolean = false
  ) {
    bulkReleaseResourceAllocations(input: $input) {
      resourceAllocations {
        ...SpecificResourceAllocation
      }
      resourceRequests {
        ...SpecificResourceRequest
        ...ResourceRequestTotalsFragment
        ...ResourceMatchesFragment
      }
    }
  }
  ${specificResourceAllocationFragment}
  ${SpecificResourceRequestFragmentDoc}
  ${resourceRequestTotalsFragment}
  ${resourceMatchesFragment}
`;

export const getFilteredAllocatedUserAndRolesList = ({
  allocatedUsersAndRoles,
  releasedAllocationsIds,
  userId
}) =>
  allocatedUsersAndRoles.reduce((acc, userObj) => {
    if (userObj.user.id !== userId) {
      acc.push(userObj);

      return acc;
    }

    const filteredRoles = (userObj.roles || [])
      .map(role => {
        const updatedRole = { ...role };

        updatedRole.resourceAllocationReference = role.resourceAllocationReference.filter(
          // eslint-disable-next-line max-nested-callbacks
          reference => !releasedAllocationsIds.includes(reference.id)
        );

        return updatedRole.resourceAllocationReference.length > 0
          ? updatedRole
          : null;
      })
      .filter(Boolean);

    if (filteredRoles.length > 0) {
      acc.push({ ...userObj, roles: filteredRoles });
    }

    return acc;
  }, []);

export const updateResourceRequestQueryCache = ({
  proxy,
  resourceAllocations,
  resourceRequests,
  resourceRequestCacheUpdateProps: { resourceRequestsQueryVariables },
  isPsaPraaResourceMatchingEnabled
}) => {
  if (resourceRequests.length === 0) return;

  try {
    const cachedData = proxy.readQuery({
      query: RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
      variables: resourceRequestsQueryVariables
    });

    if (cachedData) {
      const { resourceRequests: data } = cachedData;
      const cachedResourceRequests = data.resourceRequests;

      proxy.writeQuery({
        query: RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
        variables: resourceRequestsQueryVariables,
        data: {
          resourceRequests: {
            ...data,
            resourceRequests: resourceRequests.reduce(
              (acc, resourceRequest) => {
                const cachedRequest = acc.find(
                  request => request.id === resourceRequest.id
                );

                if (cachedRequest) {
                  const newResourceRequest = {
                    ...cachedRequest,
                    ...(isPsaPraaResourceMatchingEnabled
                      ? {
                          resourceMatches: cachedRequest.resourceMatches,
                          resourceMatchesStatus:
                            cachedRequest.resourceMatchesStatus
                        }
                      : {}),
                    resourceAllocations: resourceAllocations.filter(
                      allocation =>
                        allocation.resourceRequestId === resourceRequest.id
                    )
                  };

                  return acc.map(req =>
                    req.id === newResourceRequest.id ? newResourceRequest : req
                  );
                }

                if (
                  resourceRequest.requestStatus ===
                  ResourceRequestStatus.Submitted
                ) {
                  return [
                    ...acc,
                    {
                      ...resourceRequest,
                      resourceAllocations: [],
                      ...(isPsaPraaResourceMatchingEnabled
                        ? {
                            resourceMatches: resourceRequest.resourceMatches,
                            resourceMatchesStatus:
                              resourceRequest.resourceMatchesStatus
                          }
                        : {})
                    }
                  ];
                }

                return acc;
              },
              cachedResourceRequests
            )
          }
        }
      });
    }
    // eslint-disable-next-line no-empty
  } catch (e) {}
};

export const getReleaseResourceSnackBarMessageId = ({
  asOfDate,
  releasedRoles,
  isPsaRmpTaskAllocation1Enabled
}) => {
  if (isPsaRmpTaskAllocation1Enabled) {
    return asOfDate
      ? 'resourceAllocationChangeSnackBar.releaseResourceAsOfForNoRole'
      : 'resourceAllocationChangeSnackBar.releaseResourceForNoRole';
  }

  return asOfDate
    ? releasedRoles.length === 1
      ? 'resourceAllocationChangeSnackBar.releaseResourceAsOf'
      : 'resourceAllocationChangeSnackBar.releaseResourceMultipleRolesAsOf'
    : releasedRoles.length === 1
    ? 'resourceAllocationChangeSnackBar.releaseResource'
    : 'resourceAllocationChangeSnackBar.releaseResourceMultipleRoles';
};

export const setSnackBarMessage = ({
  asOfDate,
  userName,
  releasedRoles,
  setSnackBarState,
  formatMessage,
  isPsaRmpTaskAllocation1Enabled
}) => {
  const snackBarMessageId = getReleaseResourceSnackBarMessageId({
    asOfDate: Boolean(asOfDate),
    releasedRoles,
    isPsaRmpTaskAllocation1Enabled
  });

  const messageValues = {
    username: userName,
    date: asOfDate,
    role: isPsaRmpTaskAllocation1Enabled
      ? undefined
      : releasedRoles[0] ||
        formatMessage({ id: 'resourceAllocationChangeSnackBar.noRole' }),
    count: releasedRoles.length
  };

  setSnackBarState({
    open: true,
    message: formatMessage({ id: snackBarMessageId }, messageValues)
  });
};

export const updateAllocatedUsersCacheOnDelete = ({
  handleRemoveResourceAllocation,
  userId,
  userName,
  releasedResourcesInfo: {
    selectedAllocations,
    releaseType,
    formattedReleaseDate
  },
  resourcePlanAllocatedUsersAndRolesCacheVariables,
  resourceRequestCacheUpdateProps,
  setSnackBarState,
  formatMessage,
  isPsaPraaResourceMatchingEnabled,
  isPsaRmpTaskAllocation1Enabled
}) => (proxy, mutationResponse) => {
  const {
    data: {
      bulkReleaseResourceAllocations: { resourceRequests, resourceAllocations }
    }
  } = mutationResponse;

  const releasedRoles = selectedAllocations.map(
    allocation => allocation.role?.displayText
  );

  if (
    releaseType === ReleaseResourceAllocationType.Asof &&
    resourceAllocations.length !== 0
  ) {
    setSnackBarMessage({
      userName,
      releasedRoles,
      asOfDate: formattedReleaseDate,
      setSnackBarState,
      formatMessage,
      isPsaRmpTaskAllocation1Enabled
    });
  }

  const releasedAllocationsIdsFromInput = selectedAllocations.map(
    allocation => allocation.id
  );
  const deletedAllocationIds = releasedAllocationsIdsFromInput.filter(
    id => !resourceAllocations.some(alloc => alloc.id === id)
  );

  if (deletedAllocationIds.length === 0) return;
  const cachedResults = tryResourcePlanAllocatedUsersAndRolesCachedQuery({
    proxy,
    variables: resourcePlanAllocatedUsersAndRolesCacheVariables
  });

  if (
    cachedResults?.project.resourcePlanAllocatedUsersAndRoles.some(
      ({ user }) => user.id === userId
    )
  ) {
    const {
      project: { resourcePlanAllocatedUsersAndRoles: allocatedUsersAndRoles }
    } = cachedResults;

    const updateAllocatedUsersAndRoles = getFilteredAllocatedUserAndRolesList({
      allocatedUsersAndRoles,
      releasedAllocationsIds: deletedAllocationIds,
      userId
    });

    if (allocatedUsersAndRoles.length !== updateAllocatedUsersAndRoles.length) {
      handleRemoveResourceAllocation(userId);
    }
    setSnackBarMessage({
      userName,
      releasedRoles,
      setSnackBarState,
      formatMessage,
      isPsaRmpTaskAllocation1Enabled
    });

    proxy.writeQuery({
      query: GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES,
      variables: resourcePlanAllocatedUsersAndRolesCacheVariables,
      data: {
        project: {
          ...cachedResults.project,
          resourcePlanAllocatedUsersAndRoles: updateAllocatedUsersAndRoles
        }
      }
    });

    updateResourceRequestQueryCache({
      proxy,
      resourceAllocations,
      resourceRequestCacheUpdateProps,
      resourceRequests,
      isPsaPraaResourceMatchingEnabled
    });
  }
};

const useBulkReleaseResourceAllocations = ({
  handleRemoveResourceAllocation,
  projectId,
  userId,
  resourcePlanAllocatedUsersAndRolesCacheVariables,
  resourceRequestCacheUpdateProps,
  userName,
  setSnackBarState,
  sendNotification
}) => {
  const {
    featureFlags: {
      isPsaRmpTaskAllocation1Enabled,
      isPsaPraaResourceMatchingEnabled
    }
  } = useMeContext();

  const { dateRange } = useResourcingChartToolbarContext();
  const [releaseAllocations, { data, loading }] = useMutation(
    RELEASE_RESOURCE_ALLOCATION,
    {
      refetchQueries: [
        'getProjectTotalsBySlug',
        ...(isPsaRmpTaskAllocation1Enabled
          ? [
              getTaskResourceUserAllocationsSummaryForUserQuery({
                userId,
                projectId,
                dateRange
              })
            ]
          : [])
      ]
    }
  );
  const { formatMessage } = useIntl();

  const releaseResourceAllocationCallback = useCallback(
    releasedResourcesInfo =>
      releaseAllocations({
        variables: {
          input: {
            releaseType: releasedResourcesInfo.releaseType,
            allocationIds: releasedResourcesInfo.selectedAllocations.map(
              allocation => allocation.id
            ),
            releaseDate: releasedResourcesInfo.releaseDate
              ? dateToMidnightUTCObject(releasedResourcesInfo.releaseDate)
              : null,
            comment: releasedResourcesInfo.comment,
            sendNotification
          },
          resourceMatchesPage: 1,
          resourceMatchesPageSize: 1,
          isPsaPraaResourceMatchingEnabled
        },
        update:
          resourcePlanAllocatedUsersAndRolesCacheVariables &&
          updateAllocatedUsersCacheOnDelete({
            handleRemoveResourceAllocation,
            userId,
            userName,
            resourcePlanAllocatedUsersAndRolesCacheVariables,
            resourceRequestCacheUpdateProps,
            releasedResourcesInfo,
            setSnackBarState,
            formatMessage,
            isPsaPraaResourceMatchingEnabled,
            isPsaRmpTaskAllocation1Enabled
          })
      }),
    [
      formatMessage,
      handleRemoveResourceAllocation,
      releaseAllocations,
      resourcePlanAllocatedUsersAndRolesCacheVariables,
      resourceRequestCacheUpdateProps,
      sendNotification,
      setSnackBarState,
      userId,
      userName,
      isPsaPraaResourceMatchingEnabled,
      isPsaRmpTaskAllocation1Enabled
    ]
  );

  return {
    releaseResourceAllocation: releaseResourceAllocationCallback,
    data,
    loading
  };
};

export default useBulkReleaseResourceAllocations;
